import { html } from 'lit';
import {
  isDiagnosticLog,
  renderCallDiagnostic,
  formatDiagnosticAsText,
  type CallDiagnosticLog,
} from './CallDiagnostics.js';

// =============================================================================
// Call transcript rows — shared by the Test Lab Call Judge and the Call Details page
// =============================================================================
// The compacted turn list built by server/call-judge.ts buildTurns(): numbered rows "Agent" / "Caller" /
// "→ tool" (dashed box, tool name + arguments) / "← result" (long text scrolls inside its row), in call order.
//
//   - Test Lab passes `notesFor`: every row gets the judge's margin notes beside it, and a row that carries a
//     finding gets the red ring. Its markup and classes are exactly what TestLabPage rendered inline before.
//   - Call Details passes nothing: the plain conversation — no notes column, no ring, no verdict colour.
// =============================================================================

/** One row of the transcript, as buildTurns() returns it over the wire (JSON). */
export interface CallTranscriptTurn {
  i: number;
  ts?: number;
  kind: string;
  text: string;
  tool?: string;
  clock?: string;
  partial?: boolean;
  recovered?: boolean;
}

export interface CallTranscriptOptions {
  diagnosticLogs?: CallDiagnosticLog[];
  /** The judge's rendered margin notes for one row (Test Lab). Omitted = the plain conversation (Call Details). */
  notesFor?: (turn: CallTranscriptTurn) => unknown[];
}

// `<break time="1s"/>` is a TTS pause directive, not speech. It belongs in the raw log, not in
// a transcript someone is reading to find out what the caller actually heard.
export const spokenText = (t: unknown) =>
  String(t ?? '')
    .replace(/<break[^>]*\/>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const transcriptTurnStyle = (kind: string) =>
  kind === 'caller'
    ? { who: 'Caller', box: 'bg-base-200/70 border-base-300', name: 'text-base-content/70' }
    : kind === 'agent'
      ? // NOT bg-primary/5 — primary is ZwickRöll red here, so every line the agent spoke came
        // out pink and read as flagged. Red is reserved for turns that actually carry a finding.
        { who: 'Agent', box: 'bg-base-100 border-base-300', name: 'text-primary' }
      : kind === 'tool-call'
        ? { who: '→ tool', box: 'bg-base-100 border-base-300 border-dashed', name: 'text-base-content/40' }
        : { who: '← result', box: 'bg-base-100 border-base-300', name: 'text-base-content/40' };

// Spoken lines in the plain Call Details view (requested by ZwickRöll 16/09): the agent's words in ZwickRöll red, the
// caller's words bold black, so the two voices separate at a glance. The Test Lab keeps its neutral text, because
// red there marks a judge finding.
const textClass = (kind: string, annotated: boolean) =>
  annotated
    ? 'text-base-content/85'
    : kind === 'agent'
      ? 'text-primary font-medium'
      : kind === 'caller'
        ? 'text-base-content font-semibold'
        : 'text-base-content/85';

/** The turns inside a transcript query answer (`{ callID, turns }` as JSON). Anything else is no turns. */
export const parseCallTranscript = (raw: unknown): CallTranscriptTurn[] => {
  try {
    const d = JSON.parse(String(raw ?? '{}'));
    return Array.isArray(d?.turns) ? d.turns : [];
  } catch {
    return [];
  }
};

/** The conversation as plain text for the clipboard: the same numbers, labels and cleaned text as on screen. */
export const formatCallTranscriptAsText = (turns: CallTranscriptTurn[]): string =>
  turns
    .map(t => `${String(t.i).padStart(3)}  ${transcriptTurnStyle(t.kind).who.padEnd(8)}  ${spokenText(t.text)}`)
    .join('\n');

export function transcriptWithDiagnostics(turns: CallTranscriptTurn[], logs: CallDiagnosticLog[] = []) {
  return [
    ...turns.map(turn => ({ ts: turn.ts ?? 0, turn, log: undefined as CallDiagnosticLog | undefined })),
    ...logs.filter(isDiagnosticLog).map(log => ({
      ts: log.timestamp ? new Date(log.timestamp).getTime() : 0,
      turn: undefined as CallTranscriptTurn | undefined,
      log,
    })),
  ].sort((a, b) => a.ts - b.ts);
}
export const formatConversationWithDiagnostics = (turns: CallTranscriptTurn[], logs: CallDiagnosticLog[]) =>
  transcriptWithDiagnostics(turns, logs)
    .map(row => (row.turn ? formatCallTranscriptAsText([row.turn]) : formatDiagnosticAsText(row.log!)))
    .join('\n');

export const renderCallTranscript = (turns: CallTranscriptTurn[], options: CallTranscriptOptions = {}) => {
  const { notesFor } = options;
  return html`<div class="flex flex-col gap-1.5">
    ${transcriptWithDiagnostics(turns, options.diagnosticLogs).map(rowData => {
      if (rowData.log) return renderCallDiagnostic(rowData.log);
      const t = rowData.turn!;
      const st = transcriptTurnStyle(t.kind);
      const notes = notesFor ? notesFor(t) : null;
      // The plain view sits in a half-width column (a phone's full width): a long e-mail address or tool argument
      // wraps instead of running out of its box, and below `sm` the text moves under its number and label so it gets
      // the row's full width (beside them it kept ~80 px on a 375 px phone). The Test Lab keeps its classes unchanged.
      const row = html`<div class="rounded-lg border p-2.5 ${st.box} ${notes?.length ? 'ring-1 ring-error/30' : ''}">
        <div
          class="${notes
            ? 'flex items-baseline gap-2'
            : 'flex flex-wrap sm:flex-nowrap items-baseline gap-x-2 gap-y-0.5'}"
        >
          <span class="text-[10px] text-base-content/30 tabular-nums shrink-0 w-6 text-right">${t.i}</span>
          <span class="text-[11px] font-semibold shrink-0 w-14 ${st.name}">${st.who}</span>
          <span
            class="text-xs leading-snug ${t.kind === 'tool-call' || t.kind === 'tool-result'
              ? 'font-mono text-[11px] text-base-content/55 max-h-24 overflow-y-auto'
              : textClass(t.kind, !!notes)} ${notes ? '' : 'min-w-0 wrap-break-word basis-full sm:basis-auto'}"
            >${spokenText(t.text)}</span
          >
        </div>
      </div>`;
      return notes
        ? html`<div class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_19rem] gap-1.5 md:gap-3">
            ${row}
            <div class="flex flex-col gap-1.5">${notes}</div>
          </div>`
        : row;
    })}
  </div>`;
};
